import styled from '@emotion/styled';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { navigationService, connectToAnExpertService } from 'src/config/rootService';

import {
    getCategorySelections,
    getMostRecentCareRecommendation,
    getUserInformation,
} from 'src/modules/user/selectors/userSelectors';

import {
    Button,
    ButtonGroup,
    CalloutInline,
    Input,
    InputType,
    HeadingText50,
    BodyTextMedium,
} from '@modernhealth/vital-components';

const RequestPsychiatryProviderPage: FC = () => {
    const tags = ['pamm-initial-match-request'];
    const [reasonForRequest, setReasonForRequest] = useState('');

    const { t } = useTranslation();

    const user = useSelector(getUserInformation);
    const careRecUuid = useSelector(getMostRecentCareRecommendation)?.uuid ?? null;
    const categories = useSelector(getCategorySelections);
    const phoneNumber = user?.phoneNumber ?? '';

    const handleSubmit = async () => {
        const handleSuccess = () => {
            navigationService.navigate('/schedule-session/request-provider-success');
        };
        await connectToAnExpertService.submitForm(
            {
                userInfo: user,
                anythingElse: reasonForRequest,
                phoneNumber,
                therapistRequested: false,
                psychiatristRequested: true,
                categories,
                careRecUuid,
                tags,
            },
            handleSuccess,
        );
    };

    return (
        <MainContainer>
            <Container>
                <HeadingText50 centered={false}>
                    {t('providers:pamm_provider.request.title')}
                </HeadingText50>
                <BodyTextMedium>{t('providers:pamm_provider.request.description')}</BodyTextMedium>

                <InputContainer>
                    <Input
                        fieldId="providerType"
                        onChange={(value: string) => setReasonForRequest(value)}
                        type={InputType.TextField}
                        label={t('providers:pamm_provider.request.combo.title')}
                        value={t('providers:pamm_provider.request.combo.provider_type')}
                        readOnly
                    />

                    <Input
                        fieldId="reasonForRequest"
                        onChange={(value: string) => setReasonForRequest(value)}
                        type={InputType.TextArea}
                        label={t('providers:pamm_provider.request.text.title')}
                        value={reasonForRequest}
                    />
                </InputContainer>

                <CalloutInline variant="default">
                    {t('providers:pamm_provider.request.callout.description')}
                </CalloutInline>

                <ButtonsContainer>
                    <ButtonGroup>
                        <Button
                            onClick={() => navigationService.navigate('/dashboard')}
                            variant="secondary"
                            size="large"
                        >
                            {t('providers:pamm_provider.request.cancel.button')}
                        </Button>
                        <Button size="large" onClick={() => handleSubmit()}>
                            {t('providers:pamm_provider.request.submit.button')}
                        </Button>
                    </ButtonGroup>
                </ButtonsContainer>
            </Container>
        </MainContainer>
    );
};

export default RequestPsychiatryProviderPage;

const MainContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const Container = styled.div({
    flexDirection: 'column',
    maxWidth: '500px',
    marginTop: 45,
    marginBottom: 98,
});

const InputContainer = styled.div({
    width: '100%',
    paddingTop: '20px',
    paddingBottom: '20px',
    '& > *': {
        paddingTop: '16px',
    },
});

const ButtonsContainer = styled.div({ width: '100%', marginTop: 24 });
